<template>
  <v-container fluid>
    <v-card class="pa-6">
      <!-- Header -->
      <v-row align="center" class="mb-4">
        <v-col>
          <div class="text-h5 font-weight-bold">
            ✏️ Edit Docket | แก้ไข Docket
          </div>
          <div class="caption grey--text">
            {{ form.client_name
            }}<span v-if="form.inquiry_topic">
              &nbsp;|&nbsp; {{ form.inquiry_topic }}</span>
          </div>
        </v-col>
        <v-col class="d-flex justify-end">
          <v-btn outlined @click="$router.push(`/incorporate-docket-view/${docketId}`)">
            <v-icon left>mdi-arrow-left</v-icon>ยกเลิก
          </v-btn>
        </v-col>
      </v-row>

      <!-- ⭐ เพิ่มใหม่ — Tabs สลับระหว่าง Internal Work / Internal Timeline
           ตามแบบ incorporate-docket-add.vue โดยไม่แตะ logic เดิมของ Edit
           เนื้อหาเดิมทั้งหมดของ Edit ถูกย้ายไปอยู่ใน v-if="tabselect == 0" -->
      <div class="mb-5">
        <v-tabs v-model="tabselect" color="#55638E" class="custom-tabs" style="border-bottom: 1px solid #E0E0E0;">
          <v-tab>Internal Work</v-tab>
          <v-tab>Internal Timeline</v-tab>
        </v-tabs>
      </div>

      <!-- ⭐ เพิ่มใหม่ — แท็บ Internal Timeline
           รอ pageLoading เสร็จก่อน เพราะ TimeLineOperations ต้องใช้ form /
           timeline_case_id ที่โหลดข้อมูลจริงมาจาก loadDocket() แล้วเท่านั้น -->
      <v-card-text v-if="tabselect == 1 && !pageLoading">
        <TimeLineOperations :showaddItem="form" :statuslist="statuslist" :reportlist="reportlist"
          :modeOperations="modeOperations" :timelineStatus="timelineStatus" :status_note="status_note"
          :visible_check="visible_check" :module_name="module_name" :timeline_case_id="timeline_case_id"
          @timelineStatus="timelineStatus = $event" :showReportInv="false" @requestStatus="handleRequestStatus"
          @update:status_note="status_note = $event" @update:visible_check="visible_check = $event" />
      </v-card-text>

      <div v-if="pageLoading" class="text-center pa-10">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <!-- ⭐ แก้ไข — เดิมเป็น v-else ตรงๆ เปลี่ยนเป็น v-else-if="tabselect == 0"
           เพื่อให้โชว์เฉพาะตอนอยู่แท็บ Internal Work เนื้อหาข้างในทั้งหมด
           ไม่ถูกแตะ/แก้ไขเลยแม้แต่จุดเดียว -->
      <v-card v-else-if="tabselect == 0" outlined class="pa-6">
        <!-- Client Header (readonly, ดึงจาก Matter — ตอน Edit ไม่ให้เปลี่ยน Matter) -->
        <v-row class="mb-2">
          <v-col cols="12" md="4">
            <v-text-field :value="form.client_name" label="Client Name | ชื่อลูกค้า" outlined dense readonly
              background-color="grey lighten-4"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field :value="entityTypeLabel" label="Entity Type | ประเภทลูกค้า" outlined dense readonly
              background-color="grey lighten-4"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field :value="form.registration_no" label="Registration No. | เลขทะเบียน" outlined dense readonly
              background-color="grey lighten-4"></v-text-field>
          </v-col>
        </v-row>

        <!-- Section: Inquiry Information -->
        <div class="form-section-title mb-3 mt-2">📋 INQUIRY INFORMATION</div>
        <v-row>
          <v-col cols="12">
            <v-text-field v-model="form.inquiry_topic" label="Inquiry Topic | หัวข้อคำสอบถาม *" outlined dense
              placeholder="หัวข้อคำสอบถาม" :rules="[rules.required]"></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.date_received" label="Date Received | วันที่รับเรื่อง *" outlined dense
              type="date" :rules="[rules.required]"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.date_submitted" label="Date Submitted | วันที่ส่งคำขอ" outlined dense
              type="date"></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <!-- ⭐ แก้ไข — บังคับกรอก Next Due Date เพราะหน้า Incorporate
                 Overview (IncorporateOverview.vue) ใช้ field นี้เป็นตัวหลัก
                 ในการคำนวณสถานะ Overdue / Due Soon และรายการ "Docket ที่
                 ใกล้ครบกำหนด" (upcomingDockets) ทั้งหมด — ถ้าเว้นว่างไว้
                 docket รายการนี้จะไม่ถูกนับรวมในหน้า Overview เลย -->
            <v-text-field v-model="form.next_due_date" label="Next Due Date | วันครบกำหนดถัดไป *" outlined dense
              type="date" :rules="[rules.required]"></v-text-field>
          </v-col>
        </v-row>

        <!-- Section: Document Category (multi-select) -->
        <div class="form-section-title mb-3 mt-4 d-flex align-center flex-wrap" style="gap: 8px">
          <span>📁 DOCUMENT CATEGORY | ชนิดเอกสาร</span>
        </div>
        <v-alert dense class="caption mb-3 business-rule-alert">
          <strong>Business Rule:</strong> เลือกได้มากกว่า 1 ประเภท (Multiple
          selection) — ไม่บังคับกรอก
        </v-alert>
        <v-row>
          <v-col cols="12" md="6" v-for="cat in docCategoryOptions" :key="cat">
            <div :class="[
              'doccat-item',
              form.doc_category.includes(cat) ? 'doccat-selected' : '',
            ]" @click="selectDocCategory(cat)">
              <div :class="[
                'doccat-checkbox',
                form.doc_category.includes(cat) ? 'doccat-checkbox-checked' : '',
              ]">
                <v-icon v-if="form.doc_category.includes(cat)" x-small color="white">mdi-check</v-icon>
              </div>
              <span class="doccat-label">{{ cat }}</span>
            </div>
          </v-col>
        </v-row>
        <!-- ⭐ เตือนถ้ามีค่าเดิมที่โหลดมาไม่ตรงกับ option ปัจจุบันสักตัวเลย
             (เช่น docCategoryOptions ถูกแก้ข้อความหลัง docket นี้ถูกสร้าง)
             เพื่อให้ผู้ใช้รู้ตัวว่าทำไมไม่เห็นช่องไหนถูกไฮไลต์ แทนที่จะงงเงียบ ๆ -->
        <v-alert v-if="unmatchedDocCategories.length > 0" dense type="warning" outlined class="caption mb-3">
          ค่าเดิมที่บันทึกไว้มี "{{ unmatchedDocCategories.join(', ') }}"
          ซึ่งไม่ตรงกับตัวเลือกปัจจุบันสักอัน — เลือกใหม่อีกครั้งเพื่ออัปเดตให้ตรง
        </v-alert>

        <!-- Section: Existing Attachments (ไฟล์ที่อัปโหลดไว้แล้ว) -->
        <div class="form-section-title mb-3 mt-4">
          📎 EXISTING ATTACHMENTS | ไฟล์เอกสารเดิม
        </div>
        <v-card outlined class="mb-4">
          <v-card-text v-if="existingAttachments.length === 0" class="caption grey--text">
            ไม่มีไฟล์เอกสารที่แนบไว้
          </v-card-text>
          <v-list v-else dense>
            <v-list-item v-for="(file, i) in existingAttachments" :key="'existing-' + i" class="attachment-row">
              <v-list-item-icon>
                <v-icon color="grey darken-1">{{ fileIcon(file) }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="caption">{{
                  file
                }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action class="d-flex flex-row" style="gap: 6px;">
                <!-- พรีวิว: เปิดแท็บใหม่ชี้ไปที่ไฟล์บน Production Storage ตรง ๆ
                     ใช้ได้กับรูปภาพและ PDF ที่เบราว์เซอร์ preview ในตัวได้เลย -->
                <v-btn outlined x-small :href="attachmentUrl(file)" target="_blank">
                  <v-icon left x-small>mdi-eye-outline</v-icon>Preview
                </v-btn>
                <v-btn outlined x-small :href="attachmentUrl(file)" :download="file" target="_blank">
                  <v-icon left x-small>mdi-download</v-icon>Download
                </v-btn>
                <v-btn outlined x-small color="red" :loading="removingFile === file"
                  @click="confirmRemoveAttachment(file)">
                  <v-icon left x-small>mdi-delete-outline</v-icon>ลบ
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card>

        <!-- Section: Upload New Document (เพิ่มไฟล์ใหม่เท่านั้น) -->
        <div class="form-section-title mb-3 mt-4">
          ➕ UPLOAD NEW DOCUMENT | เพิ่มไฟล์เอกสารใหม่
        </div>
        <v-card outlined class="pa-6 text-center upload-zone mb-4">
          <div style="font-size: 30px">📂</div>
          <div class="caption mt-2">
            คลิกเพื่อเลือกไฟล์ หรือลาก&amp;วางไฟล์ที่นี่
          </div>
          <div class="caption grey--text">
            PDF, DOCX, JPG, PNG — ขนาดไม่เกิน 25 MB — ไฟล์ที่เลือกที่นี่จะถูก
            <strong>เพิ่มต่อ</strong> จากไฟล์เดิมด้านบน ไม่ได้แทนที่
          </div>
          <v-file-input v-model="form.attachments" outlined dense multiple small-chips show-size
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" prepend-icon="mdi-paperclip" label="เลือกไฟล์เอกสารใหม่"
            class="mt-4 mx-auto" style="max-width: 480px"></v-file-input>
        </v-card>

        <!-- Section: Change Tracker -->
        <div class="form-section-title mb-3 mt-4 d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
          <div>🔄 CHANGE TRACKER | การเปลี่ยนแปลงที่เกี่ยวข้อง</div>
          <router-link v-if="form.matter_id" :to="`/incorporate-matter-view/${form.matter_id}`" target="_blank"
            class="caption d-flex align-center" style="text-decoration: none; color: #0A7EA5; font-weight: 600;">
            <v-icon x-small left color="#0A7EA5">mdi-open-in-new</v-icon>
            ดูข้อมูล Matter ต้นทาง
          </router-link>
        </div>
        <v-alert dense class="caption mb-3 business-rule-alert">
          <strong>Business Rule:</strong> เลือกประเภทการเปลี่ยนแปลงได้มากกว่า 1
          ประเภท (Multiple selection) — ข้อมูลจะดึงมาจากหน้า Matter
          ของลูกความและอัปเดตใน Docket อัตโนมัติ<br />
          เมื่อเลือกแล้ว ระบบ<strong>บังคับให้ระบุหน่วยงานที่ต้องไปดำเนินการเปลี่ยนแปลง</strong>
          (เช่น กรมพัฒนาธุรกิจการค้า, สรรพากร, ประกันสังคม ฯลฯ) ได้มากกว่า 1
          หน่วยงานต่อการเปลี่ยนแปลง
        </v-alert>

        <v-row>
          <v-col cols="12" md="4" v-for="tr in trackerOptions" :key="tr.key">
            <div :class="[
              'tracker-item',
              form.change_tracker.includes(tr.key) ? 'tracker-selected' : '',
            ]" @click="selectTracker(tr.key)">
              <div :class="[
                'doccat-checkbox',
                form.change_tracker.includes(tr.key) ? 'doccat-checkbox-checked' : '',
              ]">
                <v-icon v-if="form.change_tracker.includes(tr.key)" x-small color="white">mdi-check</v-icon>
              </div>
              <span class="doccat-label">{{ tr.label }}</span>
            </div>
          </v-col>
        </v-row>

        <!-- Detail panel ของ Change Tracker ที่เลือก -->
        <div v-for="tr in trackerOptions" :key="'detail-' + tr.key">
          <v-card v-if="form.change_tracker.includes(tr.key)" outlined class="pa-4 mt-2 mb-2" color="blue lighten-5">
            <!-- Shareholders -->
            <div v-if="tr.key === 'shareholders'">
              <div class="shares-summary mb-3">
                <div class="shares-summary-item">
                  <v-avatar size="36" color="#EFF6FF">
                    <v-icon small color="#0A7EA5">mdi-chart-donut-variant</v-icon>
                  </v-avatar>
                  <div class="shares-summary-text">
                    <span class="shares-summary-label">Number of Shares</span>
                    <span class="shares-summary-value">{{ shareholdersMaxNumber.toLocaleString() }}</span>
                  </div>
                </div>

                <v-icon class="shares-summary-arrow" color="#cbd5e1">mdi-arrow-right-thin</v-icon>

                <div class="shares-summary-item">
                  <v-avatar size="36" color="#ECFDF5">
                    <v-icon small color="#059669">mdi-account-group</v-icon>
                  </v-avatar>
                  <div class="shares-summary-text">
                    <span class="shares-summary-label">Ordinary Shares(Current Shares)</span>
                    <span class="shares-summary-value shares-summary-value--current">{{
                      shareholdersCurrentDisplay.toLocaleString()
                    }}</span>
                  </div>
                </div>

                <v-icon class="shares-summary-arrow" color="#cbd5e1">mdi-plus-thick</v-icon>

                <div class="shares-summary-item">
                  <v-avatar size="36" color="#FFF7ED">
                    <v-icon small color="#EA580C">mdi-tray-plus</v-icon>
                  </v-avatar>
                  <div class="shares-summary-text">
                    <span class="shares-summary-label">Added Ordinary Shares(Tracker)</span>
                    <span class="shares-summary-value shares-summary-value--added">{{
                      shareholdersAddedFromTracker.toLocaleString() }}</span>
                  </div>
                </div>
              </div>
              <table class="repeatable-table" style="width: 100%">
                <thead>
                  <tr>
                    <th>ชื่อ–นามสกุล</th>
                    <th>สัญชาติ</th>
                    <th>หุ้นสามัญ</th>
                    <th>หุ้นบุริมสิทธิ</th>
                    <th style="width: 40px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(sh, i) in trackerDetail.shareholders" :key="'sh' + i">
                    <td>
                      <v-text-field v-model="sh.full_name" dense hide-details solo flat
                        placeholder="ชื่อ–นามสกุล"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="sh.nationality" dense hide-details solo flat
                        placeholder="สัญชาติ"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="sh.ordinary_shares" dense hide-details solo flat type="number"
                        placeholder="0" @input="onOrdinarySharesInput(i)"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="sh.preferred_shares" dense hide-details solo flat type="number"
                        placeholder="0"></v-text-field>
                    </td>
                    <td>
                      <v-btn icon x-small color="red" @click="trackerDetail.shareholders.splice(i, 1)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
              <v-btn text small color="primary" @click="addShareholderRow">
                <v-icon left small>mdi-plus</v-icon>เพิ่มผู้ถือหุ้น
              </v-btn>
            </div>

            <!-- Directors -->
            <div v-else-if="tr.key === 'directors'">
              <!-- ⭐ เพิ่มใหม่ — preview รายชื่อ Directors เดิมของ Matter (read-only) -->
              <div v-if="directorsPreviewFiltered.length > 0" class="directors-preview mb-3">
                <div class="directors-preview-title">
                  <v-icon x-small color="#0A7EA5">mdi-eye-outline</v-icon>
                  กรรมการปัจจุบันของ Matter
                </div>
                <table class="repeatable-table" style="width: 100%">
                  <thead>
                    <tr>
                      <th>ชื่อ–นามสกุล</th>
                      <th>สัญชาติ</th>
                      <th>มีอำนาจลงนาม</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(dir, i) in directorsPreviewFiltered" :key="'dir-preview-' + i">
                      <td>{{ dir.full_name }}</td>
                      <td>{{ dir.nationality }}</td>
                      <td>{{ dir.authorized }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <table class="repeatable-table" style="width: 100%">
                <thead>
                  <tr>
                    <th>ชื่อ–นามสกุล</th>
                    <th>สัญชาติ</th>
                    <th>มีอำนาจลงนาม</th>
                    <th style="width: 40px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(dir, i) in trackerDetail.directors" :key="'dir' + i">
                    <td>
                      <v-text-field v-model="dir.full_name" dense hide-details solo flat
                        placeholder="ชื่อ–นามสกุล"></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-model="dir.nationality" dense hide-details solo flat
                        placeholder="สัญชาติ"></v-text-field>
                    </td>
                    <td>
                      <v-select v-model="dir.authorized" :items="['Yes', 'No']" dense hide-details solo flat></v-select>
                    </td>
                    <td>
                      <v-btn icon x-small color="red" @click="trackerDetail.directors.splice(i, 1)">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </table>
              <v-btn text small color="primary" @click="addDirectorRow">
                <v-icon left small>mdi-plus</v-icon>เพิ่มกรรมการ
              </v-btn>
            </div>

            <!-- Registered Capital -->
            <v-row v-else-if="tr.key === 'reg-capital'" style="max-width: 320px">
              <v-col cols="12">
                <v-text-field v-model="trackerDetail.reg_capital" label="Registered Capital (THB) — ใหม่" outlined dense
                  type="number" placeholder="0.00"></v-text-field>
              </v-col>
            </v-row>

            <!-- Paid-up Capital -->
            <v-row v-else-if="tr.key === 'paidup-capital'" style="max-width: 320px">
              <v-col cols="12">
                <v-text-field v-model="trackerDetail.paidup_capital" label="Paid-up Capital (THB) — ใหม่" outlined dense
                  type="number" placeholder="0.00"></v-text-field>
              </v-col>
            </v-row>

            <!-- Company Name -->
            <v-row v-else-if="tr.key === 'company-name'" style="max-width: 420px">
              <v-col cols="12">
                <v-text-field v-model="trackerDetail.company_name" label="Company Name — ใหม่" outlined dense
                  placeholder="ชื่อบริษัทใหม่"></v-text-field>
              </v-col>
            </v-row>

            <!-- Registered Address -->
            <v-row v-else-if="tr.key === 'reg-address'">
              <v-col cols="12">
                <v-text-field v-model="trackerDetail.reg_address" label="Registered Address — ใหม่" outlined dense
                  placeholder="ที่อยู่ใหม่"></v-text-field>
              </v-col>
            </v-row>

            <!-- Auditor -->
            <v-row v-else-if="tr.key === 'auditor'">
              <v-col cols="12" md="4">
                <v-text-field v-model="trackerDetail.audit_firm" label="Audit Firm" outlined dense
                  placeholder="ชื่อบริษัท"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="trackerDetail.auditor_name" label="Auditor Name" outlined dense
                  placeholder="ชื่อผู้สอบบัญชี"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="trackerDetail.auditor_license_no" label="License No." outlined dense
                  placeholder="เลขที่ใบอนุญาต"></v-text-field>
              </v-col>
            </v-row>

            <!-- Accountant -->
            <v-row v-else-if="tr.key === 'accountant'">
              <v-col cols="12" md="6">
                <v-text-field v-model="trackerDetail.accountant_firm" label="Accountant Firm" outlined dense
                  placeholder="ชื่อสำนักงานบัญชี"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="trackerDetail.accountant_name" label="Accountant Name" outlined dense
                  placeholder="ชื่อผู้ทำบัญชี"></v-text-field>
              </v-col>
            </v-row>

            <!-- Others -->
            <v-row v-else-if="tr.key === 'others'">
              <v-col cols="12">
                <v-textarea v-model="trackerDetail.others_detail" label="Others — รายละเอียด" outlined dense rows="2"
                  placeholder="ระบุรายละเอียดการเปลี่ยนแปลงอื่น ๆ"></v-textarea>
              </v-col>
            </v-row>

            <!-- Agency selection — บังคับเลือกอย่างน้อย 1 หน่วยงาน -->
            <div class="agency-block">
              <div class="agency-title">
                <span>🏛️ หน่วยงานที่ต้องดำเนินการ
                  <span class="red--text">*</span></span>
                <v-chip x-small label color="amber lighten-2">เลือกได้มากกว่า 1 หน่วยงาน</v-chip>
              </div>
              <v-row>
                <v-col cols="12" md="4" v-for="agency in agencyOptions" :key="agency">
                  <div :class="[
                    'agency-item',
                    isAgencySelected(tr.key, agency)
                      ? 'agency-item-selected'
                      : '',
                  ]" @click="toggleAgency(tr.key, agency)">
                    <v-checkbox :input-value="isAgencySelected(tr.key, agency)" hide-details dense
                      class="ma-0 pa-0 mr-2" @click.native.stop="toggleAgency(tr.key, agency)"></v-checkbox>
                    <span>{{ agency }}</span>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </div>

        <!-- Section: Remark -->
        <div class="form-section-title mb-3 mt-4">💬 REMARK</div>
        <v-textarea v-model="form.remark" outlined rows="3" placeholder="หมายเหตุ / Remark"></v-textarea>

        <!-- Section: Status -->
        <div class="form-section-title mb-3 mt-2">
          📌 สถานะเอกสาร | STATUS
          <span class="red--text">*</span>
        </div>
        <v-row>
          <v-col cols="12" md="4">
            <!-- ⭐ แก้ไข — เดิม items ผูกกับ statusOptions (hardcode string
                 label/value) เปลี่ยนมาผูกกับ statuslist (config จริงจาก
                 DB ตาราง client_timeline_status_config) และเพิ่ม @change
                 เรียก sendToStatus เพื่อ sync ค่ากับแท็บ Internal Timeline
                 เหมือนกับที่ incorporate-docket-add.vue ทำ — statusOptions
                 เดิมยังเก็บไว้ในไฟล์นี้ (ไม่ลบ) เผื่อมีจุดอื่นอ้างอิงอยู่
                 แค่ v-select ตัวนี้ไม่ได้ใช้กับมันแล้ว -->
            <v-select v-model="form.status" :items="statuslist" item-text="descriptions" item-value="module_id" outlined
              dense @change="sendToStatus"></v-select>
            <v-chip small label :color="getDocketStatusColor(form.status)"
              :text-color="getDocketStatusTextColor(form.status)">
              <v-icon left x-small>mdi-circle</v-icon>{{ form.status }}
            </v-chip>
          </v-col>
        </v-row>
        <!-- ============ SECTION E: การเงิน / INVOICE ============ -->
        <InvoiceSectionPanel class="mt-6" :sales-order-id.sync="formQutations.salesOrderId"
          :customer-name="formQutations.customerName" :price="formQutations.price" :currency="formQutations.currency"
          :sales-order-list="salesOrderList" :invoice-list="invoiceList" :invoice-status-list="invoiceStatusList"
          section-label="E · การเงิน & สถานะ Invoice" @select-so="onSelectSO" @preview="previewInvoice" />

      </v-card>
      <!-- Actions -->
      <v-row class="mt-4">
        <v-col class="d-flex justify-end" style="gap: 10px">
          <v-btn :class="['px-8', { 'is-locked': requestPaymentDisabled }]" color="teal" dark depressed
            :loading="loadingSave" title="เปิดใช้เมื่อมี Price + Quotation ID + Client แล้วเท่านั้น"
            @click="requestPaymentDisabled ? null : updateDocket('Payment')">
            Request Payment
          </v-btn>
          <v-btn color="#0A7EA5" dark :loading="loadingSave" @click="updateDocket('Save')">
            <v-icon left>mdi-content-save</v-icon>Update
          </v-btn>
          <v-btn outlined @click="$router.push(`/incorporate-docket-view/${docketId}`)">Cancel</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Confirm Delete Attachment Dialog -->
    <v-dialog v-model="dialogConfirmDelete" max-width="420">
      <v-card>
        <v-card-title class="text-h6">ยืนยันการลบไฟล์</v-card-title>
        <v-card-text>
          ต้องการลบไฟล์ <strong>{{ fileToDelete }}</strong> หรือไม่?
          การลบนี้จะลบไฟล์ออกจากทั้ง Production และ Backup Storage ทันที
          และไม่สามารถย้อนกลับได้
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogConfirmDelete = false">ยกเลิก</v-btn>
          <v-btn color="red" dark @click="removeExistingAttachment(fileToDelete)">ลบไฟล์</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ⭐ เพิ่มใหม่ — Timeline Update Required dialog ตามแบบ
         incorporate-docket-add.vue ใช้เตือนตอนกด Update แต่ยังไม่เคยกรอก
         Internal Timeline (timelineStatus == 0 หรือ status_note ว่าง) -->
    <v-dialog v-model="dialogDelete" max-width="600px">
      <v-card class="pa-10" style="border: 5px solid #e5e7eb;">
        <v-card-title style="padding-bottom: 20px;">
          <v-row justify="center">
            <span class="text-h4 font-weight-bold">Timeline Update Required</span>
          </v-row>
        </v-card-title>
        <br />
        <v-card-subtitle>
          <v-row justify="center" class="text-center">
            <span class="black--text text-body-1">
              Before saving this case, please update the internal timeline so the team can see the latest
              work
              status.
            </span>
          </v-row>
        </v-card-subtitle>
        <v-card-actions style="padding-top: 20px">
          <v-spacer></v-spacer>
          <v-btn depressed width="170" class="mr-3 " color="#0A7EA5" @click="deleteItemConfirm"
            style="border-radius: 20px; color: white; text-transform: none;">
            Update Timeline
          </v-btn>
          <v-btn depressed width="170" @click="closeDelete" color="red"
            style="border-radius: 20px; color: white; text-transform: none;">
            Cancel
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <SuccessDialog :status="dialogAdd" :text_color="text_color" :title="title" :message="message" />
    <SuccessDialogPush :status="dialogAddPush" :text_color="text_color" :title="title" :message="message"
      :link="link" />
  </v-container>
</template>

<script>
import api from "@/services/api"; // ⭐ เพิ่ม — ยังไม่มีใน edit เดิม ใช้เรียก timeline endpoints
import SuccessDialog from "@/components/cards/SuccessDialog";
import SuccessDialogPush from "@/components/cards/SuccessDialogPush";
import { server } from "@/services/constants";
import {
  getIncorporateDocketById,
  updateIncorporateDocket,
  // ⭐ เพิ่มใหม่ — บันทึก Change Tracker ทั้งชุด
  replaceIncorporateDocketChangeAll,
  // ⭐ เพิ่มใหม่ — ลบไฟล์แนบ 1 ไฟล์ออกจาก docket (ดู api_incorporate.js
  // ต้องเพิ่ม wrapper นี้ก่อน — เรียก DELETE /incorporate-docket/:id/attachment)
  removeIncorporateDocketAttachment,
  getIncorporateMatterById,
} from "@/services/api-dtime/api_incorporate";
// ⭐ เพิ่มใหม่ — CRUD ของ Document Category (ตารางลูก incorporate_docket_doc_category
// ไม่มี replace-all endpoint เหมือน change_tracker ต้อง diff เองตอน save)
import {
  getIncorporateDocketDocCategoryByDocketId,
  createIncorporateDocketDocCategory,
  deleteIncorporateDocketDocCategory,
} from "@/services/api-dtime/api_incorporate_docket_doc_category";
import TimeLineOperations from "../timeline/TimeLineOperations.vue"; // ⭐ เพิ่ม
import InvoiceSectionPanel from "../payment-invoice/InvoiceSectionPanel.vue";
import PDFCaseProgress from "@/utils/pdfClientTimeLine"; // ⭐ เพิ่ม
const moment = require("moment-timezone"); // ⭐ เพิ่ม

// ✅ ไฟล์ incorporate ถูกเก็บที่ Production Storage (เหมือน Matter)
// path จริงคือ {PRODUCTION_STORAGE_URL}/incorporatedocket/{docketId}/{filename}
// ตาม static route ที่ตั้งไว้ใน production storage app.js:
//   app.use("/incorporatedocket", express.static(...))
const INCORPORATE_DOCKET_FILE_URL = process.env.VUE_APP_PRODUCTION_STORAGE_URL
  ? `${process.env.VUE_APP_PRODUCTION_STORAGE_URL}/incorporatedocket`
  : "http://localhost:3001/incorporatedocket";

export default {
  name: "incorporate-docket-edit",
  components: { SuccessDialog, SuccessDialogPush, TimeLineOperations, InvoiceSectionPanel }, // ⭐ เพิ่ม TimeLineOperations
  data() {
    return {
      shareholdersNumber: 0,
      shareholdersMaxNumber: 0,
      existingShareholdersCount: 0,
      syncedShareholdersAmount: 0,
      directorsListShow: [],
      docketId: null,
      pageLoading: true,
      matterId: null,
      loadingSave: false,

      fileIncorporateUrl: INCORPORATE_DOCKET_FILE_URL,

      // ⭐ ไฟล์ที่มีอยู่แล้วบน server (จาก docket.file_attachments) — แสดงเป็น
      // รายการ read-only พร้อมปุ่ม preview/download/ลบ แยกออกจาก form.attachments
      // ซึ่งเป็น File object ใหม่ที่ยังไม่อัปโหลด (v-file-input สร้าง File object
      // จาก URL ที่มีอยู่แล้วไม่ได้ — เป็นข้อจำกัดของ browser ไม่ใช่บั๊ก จึงต้อง
      // แยก 2 state นี้ออกจากกันเสมอ)
      existingAttachments: [],
      removingFile: null,
      dialogConfirmDelete: false,
      fileToDelete: null,

      rules: {
        required: (v) =>
          (v !== null && v !== undefined && String(v).trim() !== "") ||
          "กรุณากรอกข้อมูล",
      },

      form: {
        matter_id: null,
        client_name: "",
        entity_type: "",
        registration_no: "",
        inquiry_topic: "",
        date_received: "",
        date_submitted: "",
        next_due_date: "",
        doc_category: [], // ⭐ เปลี่ยนจาก "" เป็น [] (multi-select เหมือน add.vue)
        attachments: [], // ไฟล์ใหม่ที่ยังไม่อัปโหลด (File objects)
        change_tracker: [],
        remark: "",
        status: "In Progress",
      },

      // ⭐ เพิ่มใหม่ — เก็บ Document Category ที่มีอยู่แล้วใน DB พร้อม id
      // ของแถว (จาก incorporate_docket_doc_category) ใช้เทียบตอน
      // updateDocket() ว่าอันไหนต้อง create ใหม่ อันไหนต้อง delete ออก
      // เพราะไม่มี replace-all endpoint ให้ใช้เหมือน change_tracker
      existingDocCategories: [], // [{ id, doc_category }, ...]

      // ✅ UI state เท่านั้น — ยังไม่มี table รองรับฝั่ง backend
      trackerDetail: {
        shareholders: [
          {
            full_name: "",
            nationality: "",
            ordinary_shares: 0,
            preferred_shares: 0,
          },
        ],
        directors: [{ full_name: "", nationality: "", authorized: "No" }],
        reg_capital: "",
        paidup_capital: "",
        company_name: "",
        reg_address: "",
        audit_firm: "",
        auditor_name: "",
        auditor_license_no: "",
        accountant_firm: "",
        accountant_name: "",
        others_detail: "",
      },
      // ✅ UI state: { [trackerKey]: ['agency1', 'agency2'] }
      selectedAgencies: {},

      docCategoryOptions: [
        "Company Incorporation Documents | เอกสารตั้งบริษัท",
        "Certified Copy + Registration Items + Application",
        "Memorandum of Association | บช.บริคณห์สนธิ",
        "Articles of Association | ข้อบังคับ",
        "List of Shareholders (Form BorJor.5) | บัญชีรายชื่อผู้ถือหุ้น",
        "Financial Statement Submission Forms (SorBorChor.3)",
        "Tax Information | ข้อมูลภาษี",
        "Share Certificates / Share Register | ใบหุ้น/ทะเบียนหุ้น",
        "Minutes of Board of Directors' Meeting",
        "Minutes of Extraordinary Shareholders' Meeting",
        "Minutes of Annual General Shareholders' Meeting",
        "BOI Documents / Promotion Certificate",
        "Various Licenses / Permits | ใบอนุญาตต่าง ๆ",
        "Agreements / Contracts | สัญญาต่าง ๆ",
        "Legal Opinion | ความเห็นทางกฎหมาย",
        "Work Rules | ข้อบังคับการทำงาน",
        "Due Diligence Report",
        "Others | อื่น ๆ",
      ],

      trackerOptions: [
        { key: "shareholders", label: "👥 Shareholders | ผู้ถือหุ้น" },
        { key: "directors", label: "👔 Directors | กรรมการบริษัท" },
        { key: "reg-capital", label: "💰 Registered Capital (THB)" },
        { key: "paidup-capital", label: "💵 Paid-up Capital (THB)" },
        { key: "company-name", label: "🏢 Company Name | ชื่อบริษัท" },
        { key: "reg-address", label: "📍 Registered Address (Head Office)" },
        { key: "auditor", label: "📋 Auditor | ผู้สอบบัญชี" },
        { key: "accountant", label: "📊 Accountant | ผู้ทำบัญชี" },
        { key: "others", label: "📝 Others | อื่น ๆ" },
      ],

      agencyOptions: [
        "กรมพัฒนาธุรกิจการค้า (DBD)",
        "คปภ. (OIC)",
        "ประกันสังคม (SSO)",
        "สรรพากร (Revenue)",
        "การนิคมอุตสาหกรรม (IEAT)",
        "สถานฑูต (Embassy)",
        "กรมการกงสุล (Consular)",
        "สำนักงานเขต / เทศบาล / อบต.",
        "BOI",
        "กรมศุลกากร (Customs)",
        "อื่น ๆ (Others)",
      ],

      // ⚠️ เดิมของ Edit — ไม่ได้ลบ เผื่อจุดอื่นในระบบยังอ้างอิงอยู่
      // แต่ v-select ของ Status ไม่ได้ผูกกับตัวนี้แล้ว (เปลี่ยนไปใช้ statuslist
      // ที่โหลดจาก client_timeline_status_config แทน เพื่อให้ map ไป
      // timeline_status_id ได้ตามที่ระบบ Timeline ต้องการ)
      statusOptions: [
        { label: "In Progress | กำลังดำเนินการ", value: "In Progress" },
        { label: "Complete | เสร็จสิ้น", value: "Complete" },
        {
          label: "Request Payment | ขอเรียกเก็บเงิน",
          value: "Request Payment",
        },
        { label: "Cancelled | ยกเลิก", value: "Cancelled" },
      ],

      dialogAdd: false,
      dialogAddPush: false,
      text_color: "text-h5 green--text text-center",
      title: "",
      message: "",
      link: "",

      // ⭐ เพิ่มใหม่ทั้งหมด — สำหรับ Internal Timeline tab (ตามแบบ
      // incorporate-docket-add.vue) modeOperations ส่งเป็น "Edit" เพื่อให้
      // TimeLineOperations.vue โหลดประวัติ timeline เดิมที่เคยมีมาแสดงด้วย
      tabselect: 0,
      timelineStatus: 0,
      status_note: "",
      visible_check: false,
      dialogDelete: false,
      timeline_case_id: 0,
      module_name: "Corporate",
      modeOperations: "Edit",
      reportlist: [],
      statuslist: [],
      //quotations
      formQutations: {
        salesOrderId: 0,
        price: 0,
        customerName: "",
        currency: "",
      },
      salesOrderList: [],
      invoiceList: [],
      // invoiceMenu: false,
      // invoiceMenuPay: false,
      invoiceStatusList: ["Draft", "Requested", "Invoiced", "Paid"],
      quotationscheckstatus: '',
      client_id: 0,
      client_name_show: "",
      matterdatalist: [],
      initialTrackerShareholdersCount: 0,
    };
  },
  computed: {
    entityTypeLabel() {
      const map = {
        "Limited Company": "Limited Company | บริษัทจำกัด",
        "Public Company Limited": "Public Company Limited | บริษัทมหาชนจำกัด",
        Foundation: "Foundation | มูลนิธิ",
        "Representative Office": "Representative Office | สำนักงานผู้แทนการค้า",
        Individual: "Individual | บุคคลธรรมดา",
      };
      return map[this.form.entity_type] || this.form.entity_type || "-";
    },
    requestPaymentDisabled() {
      const price = Number(this.formQutations.price);
      const salesOrderId = Number(this.formQutations.salesOrderId);
      const matterId = Number(this.form.matter_id);

      const isPriceValid = !isNaN(price) && price > 0;
      const isSalesOrderIdValid = !isNaN(salesOrderId) && salesOrderId > 0;
      const isMatterIdValid = !isNaN(matterId) && matterId > 0;

      return !(isPriceValid && isSalesOrderIdValid && isMatterIdValid);
    },
    // ⭐ เพิ่มใหม่ — รายการ Document Category ที่โหลดมาแล้วไม่ตรงกับ
    // docCategoryOptions ปัจจุบันสักตัว (เผื่อ options ถูกแก้ข้อความทีหลัง)
    unmatchedDocCategories() {
      return this.form.doc_category.filter(
        (cat) => !this.docCategoryOptions.includes(cat)
      );
    },
    shareholdersCurrentDisplay() {
      return Math.max(0, this.shareholdersNumber - this.syncedShareholdersAmount);
    },
    // ⭐ เพิ่มใหม่ — เหมือนหน้า Add
    shareholdersAddedFromTracker() {
      return this.trackerDetail.shareholders.reduce(
        (sum, sh) => sum + (Number(sh.ordinary_shares) || 0),
        0
      );
    },
    directorsPreviewFiltered() {
      const trackedIds = this.trackerDetail.directors
        .map((d) => d.matter_director_id)
        .filter((id) => id !== null && id !== undefined);

      return this.directorsListShow.filter(
        (dir) => !trackedIds.includes(dir.id)
      );
    },
  },
  async beforeCreate() {
    this.$store.state.navMenu = true;
  },
  async mounted() {
    await this.loadDataInvoice(this.$route.params.id);
    await this.loaddataSalesOrder();
    await this.loadMatterList();
    this.$hideLoader();
    this.docketId = this.$route.params.id;

    // ⭐ เพิ่ม — TimeLineOperations ใช้ timeline_case_id ตัวนี้ query ประวัติ
    // (ต้องผูกกับ id เดียวกับ docketId ของ incorporate docket เสมอ)
    this.timeline_case_id = Number(this.docketId);

    // ⭐ เพิ่ม — โหลด statuslist (client_timeline_status_config) ก่อน
    // loadDocket() เพื่อให้ sendToStatus() หา module_id ของ docket.status
    // ที่โหลดมาเจอได้ทันทีตอนซิงค์ค่าด้านล่าง
    await this.loaddataLitigationCourtStatus();

    await this.loadDocket(this.docketId); // เดิม ไม่แตะ
    await this.loadMatterShareDirectorInfo(this.form.matter_id);
    // ⭐ เพิ่มใหม่ — โหลด Document Category เดิมจากตารางลูก
    // (ต้องเรียกหลัง docketId ถูกตั้งค่าแล้วเท่านั้น)
    await this.loadDocCategories(this.docketId);

    // ⭐ เพิ่ม — ซิงค์ timelineStatus ให้ตรงกับ form.status ปัจจุบันของ
    // docket ตั้งแต่โหลดหน้าครั้งแรก (ไม่ต้องรอผู้ใช้คลิกเปลี่ยน status เอง)
    this.sendToStatus(this.form.status);

    this.pageLoading = false; // เดิม ไม่แตะ
  },
  methods: {
    onOrdinarySharesInput(index) {
      const total = this.shareholdersCurrentDisplay + this.shareholdersAddedFromTracker;

      if (total > this.shareholdersMaxNumber) {
        const row = this.trackerDetail.shareholders[index];
        const currentValue = Number(row.ordinary_shares) || 0;
        const overflow = total - this.shareholdersMaxNumber;
        const adjustedValue = Math.max(0, currentValue - overflow);

        row.ordinary_shares = adjustedValue;

        this.setupAlertDialog(
          true,
          "แจ้งเตือน",
          `จำนวนหุ้นสามัญรวม (Current + Added) เกินยอดหุ้นทั้งหมดที่มี (Number of Shares) ระบบปรับจำนวนหุ้นในแถวที่กำลังแก้ไขให้เท่ากับยอดคงเหลือให้อัตโนมัติแล้ว`,
          "text-h5 orange--text text-center",
        );
      }
    },
    async loadMatterShareDirectorInfo(matterId) {
      if (!matterId) return;
      try {
        const res = await getIncorporateMatterById(matterId);
        const matter = res.data;
        if (!matter) return;

        this.shareholdersMaxNumber = matter.number_of_shares || 0;
        this.shareholdersNumber = (matter.shareholders || []).reduce(
          (sum, sh) => sum + (Number(sh.ordinary_shares) || 0),
          0
        );
        this.existingShareholdersCount = (matter.shareholders || []).length;
        this.directorsListShow = matter.directors || [];

        this.trackerDetail.shareholders = this.trackerDetail.shareholders.map((row) => {
          const matched = (matter.shareholders || []).find(
            (ms) => (ms.full_name || "").trim().toLowerCase() === (row.full_name || "").trim().toLowerCase()
          );
          return {
            ...row,
            matter_shareholder_id: matched ? matched.id : null,
          };
        });

        // ⭐ เพิ่มใหม่ — จับคู่ trackerDetail.directors กับ matter.directors
        // ด้วยชื่อ แบบเดียวกับ shareholders เพื่อให้ backend รู้ว่าแถวไหนคือ
        // "ของเดิม ต้อง update" (มี id) กับแถวไหนคือ "ใหม่ ต้อง create" (ไม่มี id)
        this.trackerDetail.directors = this.trackerDetail.directors.map((row) => {
          const matched = (matter.directors || []).find(
            (md) => (md.full_name || "").trim().toLowerCase() === (row.full_name || "").trim().toLowerCase()
          );
          return {
            ...row,
            matter_director_id: matched ? matched.id : null,
          };
        });
      } catch (error) {
        console.error("Error loading matter shareholders/directors info:", error);
      }
    },
    async loadMatterList() {
      try {
        const res = await api.getAllIncorporateMatter();
        const list = Array.isArray(res.data) ? res.data : [];
        this.matterdatalist = list;

      } catch (error) {
        console.error("Error loading matter list:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "ไม่สามารถโหลดรายชื่อลูกค้าจาก Matter ได้",
          "text-h5 red--text text-center",
        );
      }

    },
    //quotations 
    async loaddataSalesOrder() {
      try {
        const res = await api.getQuotatinsForDocketCore(Number(localStorage.getItem(server.COMPANYID)));
        this.salesOrderList = res.data || [];

      } catch (error) {
        console.error("loaddataSalesOrder error:", error);
      }
    },
    onSelectSO(so) {
      // component ส่ง object ของ quotation ที่เลือกมาให้แล้ว (หรือ null ถ้าเคลียร์ค่า)
      if (!so) {
        this.formQutations.price = "";
        this.formQutations.customerName = "";
        this.formQutations.currency = "";
        this.quotationscheckstatus = "";
        return;
      }
      this.quotationscheckstatus = so.status;
      this.formQutations.price = Number(so.subtotal);
      this.formQutations.customerName = so.customer_name || "";
      this.formQutations.currency = so.currency || "";
    },
    async loadDataInvoice(id) {
      try {
        var dataserach = {
          module_name: 'Incorporation',
          module_id: id,
          company_id: Number(localStorage.getItem(server.COMPANYID)),
        }
        const res = await api.getQuotatinsinvoiceListCore({
          params: dataserach
        })
        this.invoiceList = res.data.data || [];
      } catch (error) {
        console.error("loadDataInvoice error:", error);
      }
    },
    //end quotattions
    previewInvoice(item) {
      const routeData = this.$router.resolve(
        `/edit-invoice/${item.inv_id_preview}/inv_h/edit`
      );
      window.open(routeData.href, '_blank');
    },
    // ⭐ เพิ่มใหม่ทั้งบล็อกนี้ — สำหรับ Internal Timeline (ก็อปแบบมาจาก
    // incorporate-docket-add.vue)
    closeDelete() {
      this.dialogDelete = false;
    },
    deleteItemConfirm() {
      this.tabselect = 1;
      this.dialogDelete = false;
    },
    handleRequestStatus(value) {
      this.form.status = value;
    },
    sendToStatus(value) {
      const selectedItem = this.statuslist.find(
        (item) => item.module_id === value
      );
      // ⭐ กัน error กรณี form.status เดิม (เช่นค่า hardcode เก่าจาก DB
      // ที่ไม่ตรงกับ module_id ไหนใน statuslist ที่โหลดมาจริง)
      if (selectedItem) {
        this.timelineStatus = Number(selectedItem.timeline_status_id);
      }
    },
    async loaddataLitigationCourtStatus() {
      const result = await api.getClientTimelineStatusCongFigByModule(
        "Corporate"
      );
      this.statuslist = (result.data || []).map((item) => ({
        ...item,
        module_id: item.module_id,
      }));
    },
    // ⭐ จบบล็อกเพิ่มใหม่สำหรับ Timeline

    async loadDocket(id) {
      try {
        const res = await getIncorporateDocketById(id);
        const docket = res.data;
        if (!docket) return;
        console.log(docket)
        this.client_id = docket.matter.id;
        this.client_name_show = docket.matter.client_name;
        this.matterId = docket.matter_id || null;
        this.form.matter_id = docket.matter_id || null;
        this.form.client_name = docket.client_name || "";
        this.form.entity_type =
          docket.matter?.entity_type || docket.matter_entity_type || "";
        this.form.registration_no =
          docket.matter?.registration_no || docket.matter_registration_no || "";
        this.form.inquiry_topic = docket.inquiry_topic || "";
        this.form.date_received = this.toDateInput(docket.date_received);
        this.form.date_submitted = this.toDateInput(docket.date_submitted);
        this.form.next_due_date = this.toDateInput(docket.next_due_date);

        // ⭐ Document Category ย้ายไปเก็บที่ incorporate_docket_doc_category แล้ว
        // docket.doc_category (column เดิม) เป็น '' เสมอตอนนี้ (deprecated)
        // ไม่ใช้อ่านค่าจากตรงนี้อีกต่อไป — โหลดจริงผ่าน loadDocCategories()
        // แยกต่างหาก (เรียกต่อจาก loadDocket() ใน mounted())

        // ⭐ FIX (บั๊กหลักที่พบ) — เดิมไฟล์นี้ไม่มีบรรทัดนี้เลย ทำให้
        // ไฟล์แนบที่เคยอัปโหลดไว้ไม่ขึ้นในหน้า Edit เลย
        // file_attachments เก็บเป็น comma-separated filename string
        this.existingAttachments = docket.file_attachments
          ? docket.file_attachments
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
          : [];

        this.form.remark = docket.remark || "";
        this.form.status = docket.status || "In Progress";
        this.formQutations.salesOrderId = docket.quotation_id;
        const so = this.salesOrderList.find(
          (item) => Number(item.quotation_id) === Number(this.formQutations.salesOrderId)
        );
        this.onSelectSO(so || null);

        // ⭐ Multi-select — โหลด Change Tracker เดิมกลับเข้าฟอร์ม จากตาราง
        // incorporate_docket_change (docket.changes มาจาก findById ที่
        // include changes + agencies ไว้แล้วฝั่ง backend) โหลดทุกรายการ
        // ที่มี ไม่ใช่แค่รายการแรกเหมือนเดิม
        if (Array.isArray(docket.changes) && docket.changes.length > 0) {
          this.form.change_tracker = docket.changes.map((c) => c.change_type);

          docket.changes.forEach((c) => {
            const detail = c.change_detail || {};

            switch (c.change_type) {
              case "shareholders": {
                const items = Array.isArray(detail.items) ? detail.items : [];
                this.trackerDetail.shareholders =
                  items.length > 0
                    ? items.map((it) => ({
                      full_name: it.full_name || "",
                      nationality: it.nationality || "",
                      ordinary_shares: it.ordinary_shares || 0,
                      preferred_shares: it.preferred_shares || 0,
                    }))
                    : this.trackerDetail.shareholders;

                // ⭐ เก็บผลรวมหุ้นสามัญของแถวที่โหลดมาจาก docket เดิม (sync เข้า Matter
                // ไปแล้วก่อนหน้านี้จากการ save ครั้งก่อน) ไว้หักออกจาก Current Shares
                // เพื่อไม่ให้นับซ้ำกับ Added (Tracker) ที่นับรวมแถวเหล่านี้ด้วยทั้งหมด
                this.syncedShareholdersAmount = items.reduce(
                  (sum, it) => sum + (Number(it.ordinary_shares) || 0),
                  0
                );
                break;
              }
              case "directors":
                this.trackerDetail.directors =
                  Array.isArray(detail.items) && detail.items.length > 0
                    ? detail.items.map((it) => ({
                      full_name: it.full_name || "",
                      nationality: it.nationality || "",
                      authorized: it.authorized || "No",
                    }))
                    : this.trackerDetail.directors;
                break;
              case "reg-capital":
                this.trackerDetail.reg_capital = detail.amount ?? "";
                break;
              case "paidup-capital":
                this.trackerDetail.paidup_capital = detail.amount ?? "";
                break;
              case "company-name":
                this.trackerDetail.company_name = detail.name || "";
                break;
              case "reg-address":
                this.trackerDetail.reg_address = detail.address || "";
                break;
              case "auditor":
                this.trackerDetail.audit_firm = detail.audit_firm || "";
                this.trackerDetail.auditor_name = detail.auditor_name || "";
                this.trackerDetail.auditor_license_no =
                  detail.auditor_license_no || "";
                break;
              case "accountant":
                this.trackerDetail.accountant_firm =
                  detail.accountant_firm || "";
                this.trackerDetail.accountant_name =
                  detail.accountant_name || "";
                break;
              case "others":
                this.trackerDetail.others_detail = detail.detail || "";
                break;
            }

            // ⭐ โหลดหน่วยงานที่เลือกไว้เดิมของ change ประเภทนี้
            this.$set(
              this.selectedAgencies,
              c.change_type,
              Array.isArray(c.agencies)
                ? c.agencies.map((a) => a.agency_name)
                : [],
            );
          });
        }
        // else: docket เก่าที่ยังไม่มีข้อมูลในตาราง incorporate_docket_change
        // จะไม่มีรายละเอียด/หน่วยงานให้กรอกต่อ — ต้องเลือกใหม่ถ้าต้องการแก้ไข
      } catch (error) {
        console.error("Error loading docket:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "ไม่สามารถโหลดข้อมูล Docket ได้",
          "text-h5 red--text text-center",
        );
      }
    },

    // ⭐ เพิ่มใหม่ — โหลด Document Category เดิมจากตารางลูก
    // (เก็บทั้ง id ไว้ใน existingDocCategories เพื่อใช้ diff ตอน save)
    // รองรับทั้ง response แบบ { success, data: [...] } และแบบ array ตรงๆ
    // เผื่อ controller ยังไม่ได้เช็คให้แน่ชัด — ถ้าพังค่อยไปแก้ backend ทีหลัง
    async loadDocCategories(docketId) {
      try {
        const res = await getIncorporateDocketDocCategoryByDocketId(docketId);
        const list = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        this.existingDocCategories = list.map((item) => ({
          id: item.id,
          doc_category: item.doc_category,
        }));
        this.form.doc_category = this.existingDocCategories.map(
          (item) => item.doc_category
        );
      } catch (error) {
        console.error("Error loading doc categories:", error);
        // ไม่ block การโหลดหน้าเพียงเพราะโหลดส่วนนี้ไม่สำเร็จ
      }
    },

    toDateInput(dateStr) {
      if (!dateStr) return "";
      try {
        return new Date(dateStr).toISOString().slice(0, 10);
      } catch {
        return "";
      }
    },

    // ⭐ สร้าง URL ไปยังไฟล์จริงบน Production Storage
    // path จริง: {PRODUCTION_STORAGE_URL}/incorporatedocket/{docketId}/{filename}
    attachmentUrl(filename) {
      return `${this.fileIncorporateUrl}/${this.docketId}/${filename}`;
    },

    // ⭐ เลือกไอคอนตามนามสกุลไฟล์ ให้พอแยกประเภทได้ในรายการ
    fileIcon(filename) {
      const ext = (filename.split(".").pop() || "").toLowerCase();
      if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext))
        return "mdi-file-image-outline";
      if (ext === "pdf") return "mdi-file-pdf-box";
      if (["doc", "docx"].includes(ext)) return "mdi-file-word-outline";
      return "mdi-file-document-outline";
    },

    // ⭐ เปิด dialog ยืนยันก่อนลบจริง (ป้องกันกดลบพลาด เพราะลบแล้วกู้คืนไม่ได้)
    confirmRemoveAttachment(filename) {
      this.fileToDelete = filename;
      this.dialogConfirmDelete = true;
    },

    // ⭐ ลบไฟล์แนบ 1 ไฟล์จริง — เรียก backend ที่จะลบทั้ง local file,
    // DB column file_attachments, และ sync ลบที่ Production/Backup Storage
    // ให้ครบในคำเดียว (ดู incorporate_docket.controller.js removeAttachment)
    async removeExistingAttachment(filename) {
      this.removingFile = filename;
      this.dialogConfirmDelete = false;
      try {
        await removeIncorporateDocketAttachment(this.docketId, filename);
        this.existingAttachments = this.existingAttachments.filter(
          (f) => f !== filename,
        );
        this.setupAlertDialog(
          true,
          "Success",
          "ลบไฟล์เรียบร้อยแล้ว",
          "text-h5 green--text text-center",
        );
      } catch (error) {
        console.error("Error removing attachment:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "ไม่สามารถลบไฟล์ได้ กรุณาลองใหม่",
          "text-h5 red--text text-center",
        );
      } finally {
        this.removingFile = null;
        this.fileToDelete = null;
      }
    },

    // ⭐ Multi-select — คลิกเพื่อเพิ่ม/เอาออกจากรายการที่เลือก
    selectDocCategory(cat) {
      const idx = this.form.doc_category.indexOf(cat);
      if (idx >= 0) {
        this.form.doc_category.splice(idx, 1);
      } else {
        this.form.doc_category.push(cat);
      }
    },

    // ⭐ Multi-select — คลิกเพื่อเพิ่ม/เอาออกจากรายการที่เลือก
    // ตอนเอาออก ต้องล้าง selectedAgencies ของ key นั้นด้วย (ตัวอื่นที่เลือกอยู่ไม่กระทบ)
    selectTracker(key) {
      const idx = this.form.change_tracker.indexOf(key);
      if (idx >= 0) {
        this.form.change_tracker.splice(idx, 1);
        this.$delete(this.selectedAgencies, key);
      } else {
        this.form.change_tracker.push(key);
        this.$set(this.selectedAgencies, key, []);
      }
    },

    isAgencySelected(trackerKey, agency) {
      return (this.selectedAgencies[trackerKey] || []).includes(agency);
    },

    toggleAgency(trackerKey, agency) {
      if (!this.selectedAgencies[trackerKey]) {
        this.$set(this.selectedAgencies, trackerKey, []);
      }
      const list = this.selectedAgencies[trackerKey];
      const idx = list.indexOf(agency);
      if (idx >= 0) {
        list.splice(idx, 1);
      } else {
        list.push(agency);
      }
    },

    addShareholderRow() {
      this.trackerDetail.shareholders.push({
        full_name: "",
        nationality: "",
        ordinary_shares: 0,
        preferred_shares: 0,
      });
    },
    addDirectorRow() {
      this.trackerDetail.directors.push({
        full_name: "",
        nationality: "",
        authorized: "No",
      });
    },

    getDocketStatusColor(status) {
      const map = {
        "In Progress": "blue lighten-4",
        Complete: "green lighten-4",
        "Request Payment": "pink lighten-4",
        Cancelled: "red lighten-4",
      };
      return map[status] || "grey lighten-3";
    },
    getDocketStatusTextColor(status) {
      const map = {
        "In Progress": "blue darken-2",
        Complete: "green darken-2",
        "Request Payment": "pink darken-2",
        Cancelled: "red darken-2",
      };
      return map[status] || "grey darken-1";
    },

    validateForm() {
      if (!this.form.inquiry_topic || !this.form.inquiry_topic.trim()) {
        this.setupAlertDialog(
          true,
          "Check information",
          "กรุณากรอก Inquiry Topic",
          "text-h5 red--text text-center",
        );
        return false;
      }
      if (!this.form.date_received) {
        this.setupAlertDialog(
          true,
          "Check information",
          "กรุณากรอก Date Received",
          "text-h5 red--text text-center",
        );
        return false;
      }
      // ⭐ เพิ่มใหม่ — บังคับกรอก Next Due Date เพราะหน้า Incorporate
      // Overview ใช้ field นี้เป็นตัวหลักในการคำนวณ Overdue / Due Soon
      // และรายการ "Docket ที่ใกล้ครบกำหนด" ทั้งหมด (ดู upcomingDockets,
      // isOverdue ใน IncorporateOverview.vue) ถ้าเว้นว่างไว้ docket นี้
      // จะไม่ถูกนับรวมในหน้า Overview เลยโดยไม่มีการแจ้งเตือนใด ๆ
      if (!this.form.next_due_date) {
        this.setupAlertDialog(
          true,
          "Check information",
          "กรุณากรอก Next Due Date | วันครบกำหนดถัดไป (ใช้คำนวณสถานะ Overdue/Due Soon ในหน้า Overview)",
          "text-h5 red--text text-center",
        );
        return false;
      }
      for (const key of this.form.change_tracker) {
        const agencies = this.selectedAgencies[key] || [];
        if (agencies.length === 0) {
          const label =
            this.trackerOptions.find((t) => t.key === key)?.label || key;
          this.setupAlertDialog(
            true,
            "Check information",
            `กรุณาเลือกหน่วยงานที่ต้องดำเนินการสำหรับ "${label}" อย่างน้อย 1 หน่วยงาน`,
            "text-h5 red--text text-center",
          );
          return false;
        }
      }
      return true;
    },

    buildFormData() {
      const fd = new FormData();
      const company_id = localStorage.getItem(server.COMPANYID);
      const user_id = localStorage.getItem(server.USER_ID);

      if (this.form.matter_id) fd.append("matter_id", this.form.matter_id);
      fd.append("client_name", this.form.client_name);
      fd.append("inquiry_topic", this.form.inquiry_topic);
      // ⭐ เหมือนหน้า Add — column เดิมเก็บเป็น '' เสมอ ข้อมูลจริงอยู่ตารางลูก
      // (incorporate_docket_doc_category / incorporate_docket_change)
      fd.append("doc_category", "");
      fd.append("change_tracker", "");

      // ⭐ แก้ไข (รอบ 2 — หลังตรวจ repository จริงแล้ว) — date_received และ
      // next_due_date ผ่านการบังคับกรอกจาก validateForm() มาแล้วเสมอ จึงส่ง
      // ค่าตรง ๆ ได้เลย ส่วน date_submitted (ไม่บังคับกรอก) ส่งกลับไปแบบเดิม
      // เสมอ (value || "") แทนที่จะ "ไม่ append field เลยเมื่อว่าง" ตามที่เคย
      // แก้ไว้รอบแรก เพราะพบว่า incorporate_docket.repository.js ฝั่ง
      // exports.update() ใช้ Sequelize Model.update() ซึ่งจะ "ข้าม field ที่
      // เป็น undefined ไปเฉย ๆ ไม่แตะค่าเดิมใน DB เลย" (ยืนยันจาก pattern
      // invoice_id ในไฟล์เดียวกัน: `data.invoice_id !== undefined ? ... :
      // undefined` ที่ตั้งใจอาศัยพฤติกรรมนี้) — ถ้าไม่ append field ตอนผู้ใช้
      // ลบ date_submitted ที่เคยกรอกไว้จนว่างเพื่อ "ล้างค่า" (เคสสำคัญของหน้า
      // Edit นี้โดยเฉพาะ) DB จะไม่ถูกล้างให้เลย จึงย้ายหน้าที่ sanitize
      // "" → null ไปทำที่ repository แทน (ดู sanitizeDateOnly() ใน
      // incorporate_docket.repository.js) แล้วให้ frontend ส่งค่าตรง ๆ
      // เสมอแบบง่าย ๆ พอ — วิธีนี้ทำให้ "ล้างค่า date_submitted" ทำงานถูกต้อง
      fd.append("date_received", this.form.date_received);
      fd.append("next_due_date", this.form.next_due_date);
      fd.append("date_submitted", this.form.date_submitted || "");

      fd.append("status", this.form.status);
      fd.append("remark", this.form.remark || "");
      fd.append("company_id", company_id);
      fd.append("user_update", user_id);
      fd.append("quotation_id", Number(this.formQutations.salesOrderId));

      // ⭐ เฉพาะไฟล์ใหม่ (form.attachments) เท่านั้นที่ส่งเป็น multipart
      // ไฟล์เดิม (existingAttachments) ไม่ต้องส่งซ้ำ — backend ฝั่ง update
      // จะ append ไฟล์ใหม่ต่อจาก file_attachments เดิมที่มีอยู่ในแล้วอัตโนมัติ
      // (ดู incorporate_docket.controller.js exports.update)
      if (this.form.attachments && this.form.attachments.length > 0) {
        this.form.attachments.forEach((file) => fd.append("file", file));
      }

      return fd;
    },

    buildChangesPayload() {
      return this.form.change_tracker.map((key) => {
        let change_detail = {};

        switch (key) {
          case "shareholders":
            change_detail = {
              items: this.trackerDetail.shareholders
                .filter((s) => s.full_name && s.full_name.trim() !== "")
                .map((s, index) => ({
                  full_name: s.full_name,
                  nationality: s.nationality,
                  ordinary_shares: Number(s.ordinary_shares) || 0,
                  preferred_shares: Number(s.preferred_shares) || 0,
                  sort_order: this.existingShareholdersCount + index,
                  matter_shareholder_id: s.matter_shareholder_id || null,   // ⭐ เพิ่มใหม่
                })),
            };
            break;
          case "directors":
            change_detail = {
              items: this.trackerDetail.directors
                .filter((d) => d.full_name && d.full_name.trim() !== "")
                .map((d) => ({
                  full_name: d.full_name,
                  nationality: d.nationality,
                  authorized: d.authorized,
                  matter_director_id: d.matter_director_id || null,   // ⭐ เพิ่มใหม่
                })),
            };
            break;
          case "reg-capital":
            change_detail = {
              amount: Number(this.trackerDetail.reg_capital) || 0,
            };
            break;
          case "paidup-capital":
            change_detail = {
              amount: Number(this.trackerDetail.paidup_capital) || 0,
            };
            break;
          case "company-name":
            change_detail = { name: this.trackerDetail.company_name || "" };
            break;
          case "reg-address":
            change_detail = { address: this.trackerDetail.reg_address || "" };
            break;
          case "auditor":
            change_detail = {
              audit_firm: this.trackerDetail.audit_firm || "",
              auditor_name: this.trackerDetail.auditor_name || "",
              auditor_license_no: this.trackerDetail.auditor_license_no || "",
            };
            break;
          case "accountant":
            change_detail = {
              accountant_firm: this.trackerDetail.accountant_firm || "",
              accountant_name: this.trackerDetail.accountant_name || "",
            };
            break;
          case "others":
            change_detail = { detail: this.trackerDetail.others_detail || "" };
            break;
        }

        return {
          change_type: key,
          change_detail,
          agencies: this.selectedAgencies[key] || [],
        };
      });
    },

    // ⭐ เพิ่มใหม่ — sync Document Category ด้วยการ diff เพราะไม่มี
    // replace-all endpoint (ต่างจาก Change Tracker) ต้องเทียบเองว่าอันไหน
    // ต้อง create ใหม่ อันไหนต้อง delete ออก โดยเทียบกับ existingDocCategories
    // ที่โหลดมาตอน loadDocCategories()
    async syncDocCategories(docketId) {
      const existingLabels = this.existingDocCategories.map(
        (c) => c.doc_category
      );
      const currentLabels = this.form.doc_category;

      // อันที่ถูกเอาออก (มีใน existing แต่ไม่มีใน current) → ลบ
      const toDelete = this.existingDocCategories.filter(
        (c) => !currentLabels.includes(c.doc_category)
      );
      // อันที่เพิ่มใหม่ (มีใน current แต่ไม่มีใน existing) → สร้าง
      const toAdd = currentLabels.filter(
        (cat) => !existingLabels.includes(cat)
      );

      const user_id = Number(localStorage.getItem(server.USER_ID));

      for (const item of toDelete) {
        await deleteIncorporateDocketDocCategory(item.id);
      }
      for (const cat of toAdd) {
        await createIncorporateDocketDocCategory({
          docket_id: docketId,
          doc_category: cat,
          user_create: user_id,
        });
      }
    },

    // ⭐ บันทึกแก้ไขจริง — ทำขั้นตอนต่อกัน:
    // 1) แก้ไข docket หลัก (multipart/form-data — อัปโหลดไฟล์ใหม่ถ้ามี) — เดิม
    // 2) ⭐ เพิ่ม — sync Document Category (diff add/delete)
    // 3) ⭐ เพิ่ม — สร้าง timeline record ใหม่ + regenerate PDF client
    //    progress ผูกกับ docketId เดิม (มีอยู่แล้วตั้งแต่เปิดหน้า Edit
    //    ต่างจาก add.vue ที่ต้องรอสร้าง docket ก่อนถึงจะมี id)
    // 4) replace-all Change Tracker ทั้งชุด — เดิม

    async updateDocket(mode) {
      if (!this.validateForm()) return; // เดิม ไม่แตะ
      // ✅ ตรวจว่าลูกค้าใน Quotation ตรงกับลูกค้าของ Matter ที่ผูกกับ Docket นี้
      if (this.formQutations.salesOrderId) {
        const selectedSO = this.salesOrderList.find(
          (item) => Number(item.quotation_id) === Number(this.formQutations.salesOrderId)
        );

        if (!selectedSO) {
          this.setupAlertDialog(
            true,
            "Check information",
            "ไม่พบใบเสนอราคาที่เลือก กรุณาเลือกใหม่อีกครั้ง",
            "text-h5 red--text text-center"
          );
          return;
        }

        const matterData = this.matterdatalist.find(
          (item) => Number(item.id) === Number(this.form.matter_id)
        );

        if (!matterData) {
          this.setupAlertDialog(
            true,
            "Check information",
            "ไม่พบข้อมูลลูกค้า (Matter) กรุณาตรวจสอบข้อมูลอีกครั้ง",
            "text-h5 red--text text-center"
          );
          return;
        }

        if (Number(selectedSO.client_id) !== Number(matterData.client_id)) {
          this.setupAlertDialog(
            true,
            "ข้อมูลลูกค้าไม่ตรงกัน",
            `ใบเสนอราคา ${selectedSO.doc_no} เป็นของลูกค้า "${selectedSO.customer_name || "-"}" ซึ่งไม่ตรงกับลูกค้าที่ผูกกับ Docket นี้ กรุณาเลือกใหม่ให้ถูกต้อง`,
            "text-h5 red--text text-center"
          );
          return;
        }
      }

      if (this.timelineStatus == 0 || this.status_note.trim() == "") {
        this.dialogDelete = true;
        return;
      }

      // ⭐ เพิ่ม — บังคับให้กรอก Internal Timeline ก่อน save เสมอ
      // เหมือนกับ incorporate-docket-add.vue
      if (this.timelineStatus == 0 || this.status_note.trim() == "") {
        this.dialogDelete = true;
        return;
      }

      this.loadingSave = true; // เดิม ไม่แตะ
      this.$showLoader(); // เดิม ไม่แตะ

      try {
        const fd = this.buildFormData(); // เดิม ไม่แตะ
        await updateIncorporateDocket(this.docketId, fd); // เดิม ไม่แตะ

        // ⭐ เพิ่มใหม่ — sync Document Category (diff add/delete) หลัง docket
        // หลักอัปเดตสำเร็จ ถ้า fail แจ้ง error เฉพาะจุดแล้ว return ทันที
        // เหมือน pattern เดียวกับหน้า Add (ไม่ throw ไปให้ catch ใหญ่ตอนท้าย
        // จับเงียบ ๆ เพราะข้อมูลนี้ผู้ใช้ตั้งใจเลือกเอง ต้องรู้ทันทีถ้าพัง)
        try {
          await this.syncDocCategories(this.docketId);
        } catch (docCategoryError) {
          console.error("Error syncing doc category:", docCategoryError);
          this.$hideLoader();
          this.loadingSave = false;
          this.setupAlertDialog(
            true,
            "Error",
            "บันทึก Docket สำเร็จ แต่เกิดข้อผิดพลาดในการอัปเดต Document Category กรุณาลองใหม่อีกครั้ง",
            "text-h5 red--text text-center",
          );
          return;
        }

        try {
          if (this.quotationscheckstatus == "Submitted") {

            var dataupdateStatus = {
              status: "InProgress"
            }
            await api.updateQuotationsCore(Number(this.formQutations.salesOrderId), dataupdateStatus)
          }
        } catch (quotationsError) {
          console.log(quotationsError)
        }
        // ⭐ เพิ่ม — บันทึก timeline history record ใหม่ ผูกกับ docketId
        // เดิมที่มีอยู่แล้ว (ไม่ต้องมี main_id ชั่วคราวแบบ add.vue)
        const dataadd_timeline = {
          timeline_status_id: Number(this.timelineStatus),
          case_id: Number(this.docketId),
          module_name: "Corporate",
          case_woker: Number(localStorage.getItem(server.USER_ID)),
          status_time: moment().format("YYYY-MM-DD HH:mm:ss"),
          status_note: this.status_note.trim(),
          visibility: this.visible_check,
        };
        await api.creategetClientTimelineHistory(dataadd_timeline);

        // ⭐ เพิ่ม — regenerate PDF client progress เหมือน add.vue
        // ไม่ throw ต่อถ้า fail (ตัดขาดจาก flow หลัก ไม่ให้กระทบการบันทึก
        // docket / change tracker ที่เหลือ)
        try {
          const caseDetailResult = await api.getgetTimelineDataDetail({
            params: {
              module_name: this.module_name,
              case_id: Number(this.docketId),
            },
          });
          const caseInfo = { ...(caseDetailResult.data?.[0] || {}) };

          const visibilityResult =
            await api.getgetClientTimelineHistoryShowVisibility({
              params: {
                module_name: this.module_name,
                case_id: Number(this.docketId),
              },
            });
          const timelineData = visibilityResult.data || [];

          caseInfo.status = timelineData[0]?.status || "";
          caseInfo.lastUpdated = timelineData[0]?.status_time || "";

          const latestUpdate = {
            message: timelineData[0]?.status_note || "",
            date: timelineData[0]?.status_time || "",
          };

          await PDFCaseProgress.createToPDF(
            {
              caseInfo,
              latestUpdate,
              timelineData,
              caseId: Number(this.docketId),
              moduleName: this.module_name,
            },
            { open: false }
          );
        } catch (pdfError) {
          console.error("PDF generation/upload failed:", pdfError);
          // ไม่ throw ต่อ — ตัดขาดจาก flow หลัก
        }

        // ↓↓↓ เดิม ไม่แตะ ↓↓↓
        const user_id = localStorage.getItem(server.USER_ID);
        const changes = this.buildChangesPayload();
        await replaceIncorporateDocketChangeAll(this.docketId, {
          changes,
          user_id,
          matter_id: this.form.matter_id,
        });

        this.$hideLoader();
        this.loadingSave = false;
        if (mode == "Save") {
          this.setupAlertDialogPush(
            true,
            "Success",
            "บันทึกข้อมูลสำเร็จ",
            "text-h5 green--text text-center",
            `/incorporate-docket-view/${this.docketId}`,
          );
        } else {
          var mainid = this.docketId;
          var matterid = Number(this.form.matter_id);

          var customer_list = this.matterdatalist.find(
            item => Number(item.id) === matterid
          )
          var customer_name_master_show = this.client_name_show
          var clientid = customer_list.client_id;

          var dataPush = {
            customer_name_master: customer_name_master_show,
            module_id: 0,
            quotation_id: Number(this.formQutations.salesOrderId),
          }

          dataPush.module_id = Number(mainid);

          this.$router.push({
            path: `/new-free-invoice/0/invoice/D/${clientid}/Incorporation`,
            query: dataPush,
          });
        }
      } catch (error) {
        this.$hideLoader();
        this.loadingSave = false;
        console.error("Error updating docket:", error);
        this.setupAlertDialog(
          true,
          "Error",
          "เกิดข้อผิดพลาดในการบันทึก",
          "text-h5 red--text text-center",
        );
      }
    },

    // ⭐ FIX: คอมโพเนนต์ SuccessDialog.vue ผูก v-model กับ
    // $store.state.global_dialog โดยตรง (ไม่ได้ใช้ "status" prop ที่ส่งเข้าไป
    // เลยแม้แต่จุดเดียว) ดังนั้นถ้าตั้งแค่ this.dialogAdd = status เฉย ๆ
    // popup จะไม่เด้งขึ้นมาเลย — ต้อง sync เข้า store ทุกครั้งที่เรียกใช้
    setupAlertDialog(status, title, message, text_color) {
      this.title = title;
      this.message = message;
      this.dialogAdd = status;
      this.text_color = text_color;
      this.$store.state.global_dialog = status;
    },
    setupAlertDialogPush(status, title, message, text_color, link) {
      this.title = title;
      this.message = message;
      this.dialogAddPush = status;
      this.text_color = text_color;
      this.link = link;
      this.$store.state.global_push_dialog = status;
    },
  },
};
</script>

<style scoped>
.business-rule-alert {
  background-color: #fffbeb !important;
  border: 1.5px solid #fbbf24 !important;
  color: #92400e !important;
}

.business-rule-alert>>>.v-alert__content {
  color: #92400e !important;
}

.business-rule-alert strong {
  color: #78350f !important;
}

.bg-colorth {
  background-color: #8d8d8d !important;
}

.tabletextwhite {
  color: white !important;
}

.form-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  border-left: 4px solid #0a7ea5;
  padding-left: 10px;
}

.upload-zone {
  border: 1.5px dashed #cbd5e1 !important;
  background: #f9fafb;
}

.attachment-row {
  border-bottom: 1px solid #f1f5f9;
}

.attachment-row:last-child {
  border-bottom: none;
}

/* Document Category items */
.doccat-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fff;
}

.doccat-item:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.doccat-selected {
  border-color: #2563eb !important;
  background: #eff6ff !important;
}

.doccat-label {
  font-size: 13px;
  color: #374151;
}

/* Change Tracker items */
.tracker-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fff;
}

.tracker-item:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.tracker-selected {
  border-color: #2563eb !important;
  background: #eff6ff !important;
}

/* Checkbox indicator ใช้ร่วมกันทั้ง Document Category และ Change Tracker (multi-select) */
.doccat-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #fff;
  transition: all 0.15s;
}

.doccat-checkbox-checked {
  border-color: #2563eb;
  background: #2563eb;
}

/* Repeatable table (shareholders / directors) */
.repeatable-table {
  border-collapse: collapse;
}

.repeatable-table th {
  background: #eef2ff;
  padding: 8px 10px;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  border: 1px solid #e5e7eb;
}

.repeatable-table td {
  padding: 7px 10px;
  border: 1px solid #e5e7eb;
}

/* Agency selection block */
.agency-block {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1.5px dashed #f59e0b;
  border-radius: 8px;
  background: #fffbeb;
}

.agency-title {
  font-size: 12px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.agency-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11.5px;
  color: #374151;
  border: 1.5px solid #fde68a;
  border-radius: 6px;
  padding: 7px 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}

.agency-item:hover {
  border-color: #f59e0b;
}

.agency-item-selected {
  border-color: #f59e0b !important;
  background: #fef3c7 !important;
}

.info-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #c47f00;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  cursor: help;
}

.is-locked {
  background-color: #b8e6e2 !important;
  border-color: #b8e6e2 !important;
  color: whitesmoke !important;
  opacity: 1 !important;
  pointer-events: none !important;
  cursor: not-allowed !important;
  box-shadow: none !important;
}

.shares-summary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  padding: 14px 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  max-width: 80%;
  margin: 0 auto;
}

.shares-summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.shares-summary-arrow {
  opacity: 0.6;
}

.shares-summary-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}

.shares-summary-label {
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.shares-summary-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.shares-summary-value--current {
  color: #059669;
}

.shares-summary-value--added {
  color: #EA580C;
}

.directors-preview {
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  padding: 10px 12px;
  background: #f9fafb;
}

.directors-preview-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #0A7EA5;
  margin-bottom: 8px;
}
</style>